import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Tooltip,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ViewIcon } from "@chakra-ui/icons";
import { FiRefreshCw, FiBarChart2 } from "react-icons/fi";
import { projectService } from "../services/edosService";

const STATUS_COLORS = {
  draft: "gray",
  planning: "blue",
  active: "green",
  on_hold: "orange",
  uat: "cyan",
  deployment: "purple",
  completed: "teal",
  cancelled: "red",
  archived: "blackAlpha",
};

const PRIORITY_COLORS = {
  Low: "blue",
  Medium: "yellow",
  High: "orange",
  Critical: "red",
};

const PROJECT_TYPES = [
  "Implementation",
  "Support",
  "Internal",
  "Migration",
  "Enhancement",
];

export default function ProjectsPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [projects, setProjects] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [search, setSearch] = useState("");

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType) params.type = filterType;
      if (search) params.search = search;
      const res = await projectService.list(params);
      setProjects(res.data.data || res.data.projects || []);
    } catch {
      toast({
        title: "Failed to load projects",
        status: "error",
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, search, toast]);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await projectService.metrics();
      setMetrics(res.data.data || res.data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchMetrics();
  }, [fetchProjects, fetchMetrics]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete project "${name}"?`)) return;
    try {
      await projectService.remove(id);
      toast({ title: "Project deleted", status: "success" });
      fetchProjects();
      fetchMetrics();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to delete",
        status: "error",
      });
    }
  };

  const filteredProjects = projects.filter((p) => {
    if (search && !p.projectName?.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  });

  return (
    <Box p={6}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Projects
          </Text>
          <Text color="gray.500" fontSize="sm">
            Manage all projects across the organization
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={() => {
              fetchProjects();
              fetchMetrics();
            }}
          />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => navigate("/edos/projects/create")}
          >
            New Project
          </Button>
        </HStack>
      </Flex>

      {/* Metrics */}
      {metrics && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          {[
            {
              label: "Total",
              value: metrics.total ?? metrics.totalProjects,
              color: "blue",
            },
            {
              label: "Active",
              value: metrics.active ?? metrics.activeProjects,
              color: "green",
            },
            {
              label: "On Hold",
              value: metrics.on_hold ?? metrics.onHold,
              color: "orange",
            },
            {
              label: "Completed",
              value: metrics.completed ?? metrics.completedProjects,
              color: "teal",
            },
          ].map((m) => (
            <Box
              key={m.label}
              bg={bg}
              border="1px"
              borderColor={borderColor}
              borderRadius="md"
              p={4}
            >
              <Stat>
                <StatLabel color="gray.500">{m.label}</StatLabel>
                <StatNumber color={`${m.color}.500`}>{m.value ?? 0}</StatNumber>
              </Stat>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Filters */}
      <Flex gap={3} mb={4} wrap="wrap">
        <Input
          placeholder="Search projects..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="240px"
        />
        <Select
          placeholder="All Statuses"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          maxW="180px"
        >
          {Object.keys(STATUS_COLORS).map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, " ")}
            </option>
          ))}
        </Select>
        <Select
          placeholder="All Types"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          maxW="180px"
        >
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      </Flex>

      {/* Table */}
      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : filteredProjects.length === 0 ? (
          <Alert status="info" m={4}>
            <AlertIcon />
            No projects found. Click "New Project" to create one.
          </Alert>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg={headerBg}>
              <Tr>
                <Th>Project</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Priority</Th>
                <Th>Health</Th>
                <Th>Target End</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredProjects.map((p) => (
                <Tr key={p._id} _hover={{ bg: headerBg }}>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text
                        fontWeight="semibold"
                        fontSize="sm"
                        color="blue.500"
                        cursor="pointer"
                        _hover={{ textDecoration: "underline" }}
                        onClick={() => navigate(`/edos/projects/${p._id}`)}
                      >
                        {p.projectName}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {p.projectCode}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{p.projectType}</Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLORS[p.status] || "gray"}>
                      {p.status?.replace(/_/g, " ")}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={PRIORITY_COLORS[p.priority] || "gray"}>
                      {p.priority}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={
                        p.healthStatus === "green"
                          ? "green"
                          : p.healthStatus === "red"
                            ? "red"
                            : p.healthStatus === "amber"
                              ? "orange"
                              : "gray"
                      }
                    >
                      {p.healthStatus || "N/A"}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.500">
                      {p.targetEndDate
                        ? new Date(p.targetEndDate).toLocaleDateString()
                        : "—"}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label="View Details">
                        <IconButton
                          size="xs"
                          icon={<ViewIcon />}
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="View"
                          onClick={() => navigate(`/edos/projects/${p._id}`)}
                        />
                      </Tooltip>
                      <Tooltip label="View Sprints">
                        <IconButton
                          size="xs"
                          icon={<FiBarChart2 />}
                          variant="ghost"
                          colorScheme="purple"
                          aria-label="Sprints"
                          onClick={() =>
                            navigate(`/edos/sprints?projectId=${p._id}`)
                          }
                        />
                      </Tooltip>
                      <Tooltip label="Delete">
                        <IconButton
                          size="xs"
                          icon={<DeleteIcon />}
                          variant="ghost"
                          colorScheme="red"
                          aria-label="Delete"
                          onClick={() => handleDelete(p._id, p.projectName)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
